import CustomHtmlKeyboardResponsePlugin from "../../../customPlugin/customHtmlKeyboardResponse";
import { resolveLayoutPlaceholders, applyTutorialHeaderPlaceholders } from "../../../lib/utils";
import { KEY_CODES } from "../../../gearshiftGame/constants";
import { Game } from "../../../gearshiftGame/game";
import { Score } from "../../../gearshiftGame/score";
import { hideElement } from "../../../gearshiftGame/utils";
import { createReadyOverlay, createFollowUpOverlay, createCongratsOverlay } from "../../ui/overlays";
import { injectSurveyPanel, removeSurveyPanel } from "../hq/buildHQSurveyPanel.js";
import { createPreservedFrame, prepareBoardPreview } from "./missionHelpers";

/**
 * Shared builder for tutorial timelines that replay selected phases with overlays and crash/wrong-lane loops.
 * Mission Five version with support for cooperation-choice validation.
 */
export function buildTutorialWithPhasesMissionFive({
  jsPsych,
  metadata,
  score,
  trialConfig,
  stimulus,
  phases = [1],
  buildersMap = {},
  overlayOptions = {},
  customOptions = {},
  hooks = {},
  disableAcceleration = false,
}) {
  const followUpForward = `[press "__FORWARD_KEY__" to continue ->]`;

  const resolveOverlayText = (text) => resolveLayoutPlaceholders(jsPsych, text);

  const ready = overlayOptions.ready || {};
  const congrats = overlayOptions.congrats || {};

  const readyForward = ready.forwardText ?? `[press "__FORWARD_KEY__" to begin ->]`;
  const readyBack = ready.backText ?? "";
  const readyImage = ready.imageOrVideoUrl ?? "assets/images/instructions/video_placeholder.svg";
  const readyShowText = ready.showText ?? false;

  const congratsForward = congrats.forwardText ?? `[press "__FORWARD_KEY__" to continue ->]`;
  const congratsBack = congrats.backText ?? "";
  const congratsImage = congrats.imageOrVideoUrl ?? "assets/images/instructions/video_placeholder.svg";
  const congratsForwardCrash = congrats.forwardTextCrash ?? `[press "__FORWARD_KEY__" to try again ]`;
  const congratsBackCrash = congrats.backTextCrash ?? "";
  const congratsImageCrash =
    congrats.imageOrVideoUrlCrash ?? "assets/images/instructions/try_again_placeholder.svg";
  const congratsShowText = congrats.showText ?? false;
  const followUpOnly = overlayOptions.followUpOnly ?? false;

  const timeline = [];
  const firstPhase = phases[0];
  const lastPhase = phases[phases.length - 1];

  // Helper to create a fresh Game for the current iteration
  function createFreshGame() {
    // Create a temporary Score for the tutorial so it doesn't affect the real Score
    const tutorialScore = new Score(jsPsych, score.startingLives, score.rewardReduction);

    // Create tutorial config with disableAcceleration flag
    const tutorialTrialConfig = {
      ...trialConfig,
      disableAcceleration,
    };

    // Use tutorialScore instead of SCORE
    window._gear_currentGame = new Game(jsPsych, metadata, tutorialScore, tutorialTrialConfig);
    window._gear_currentGame.isTutorial = true;
    applyTutorialHeaderPlaceholders(jsPsych);
    return window._gear_currentGame;
  }

  // m5 demands a different approach to preserving the frame on crash or wrong lane due to the nature of the tutorial phases and how the game state is managed globally. 
  function preserveFrameNow() {
    try {
      const display = document.querySelector(".jspsych-display-element") || document.body;
      const liveHtml = display.innerHTML || "";
      const htmlToUse =
        liveHtml.length >= 200
          ? liveHtml
          : (window._gear_preservedFrameHTML_phaseStart || window._gear_preservedFrameHTML || liveHtml);
      const preserved = createPreservedFrame({ html: htmlToUse, display, score });
      const existing = document.getElementById("gear-preserved-frame");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      document.body.appendChild(preserved);
      window._gear_preservedFrameHTML = preserved.innerHTML;
    } catch (e) {
      console.warn("buildTutorialWithPhasesMissionFive: preserveFrameNow failed", e);
    }
  }

  // if the first phase is not 0, add a ready overlay at the beginning of other tutorials
  if (firstPhase !== 0) {
    const readyTrial = {
      choices: "NO_KEYS",
      on_load: function () {
        window._gear_stopRemaining = false;
        window._gear_preservedFrameHTML = null;
        window._gear_preservedFrameHTML_phaseStart = null;
        window._gear_preservedFrameHTML_phaseStart_len = 0;

        const game = createFreshGame();
        prepareBoardPreview(game, firstPhase >= 1 ? 1 : 0);

        // If followUpOnly is set, show only the follow-up overlay without readyOverlay/video
        if (followUpOnly) {
          const followUpOverlay = createFollowUpOverlay({
            forwardText: resolveOverlayText(followUpForward),
            onContinue: () => {
              try {
                game.startPhase0();
                if (firstPhase >= 2) {
                  game.matchDriverAndTargetSpeed();
                  if (typeof game.updateCoordinates === "function") {
                    game.updateCoordinates();
                  }
                }
              } catch (e) {
                console.warn("Failed to initialize board:", e);
              }
              jsPsych.finishTrial();
            },
          });
          document.body.appendChild(followUpOverlay);
          return;
        }

        const overlay = createReadyOverlay({
          forwardText: resolveOverlayText(readyForward),
          backText: resolveOverlayText(readyBack),
          imageOrVideoUrl: readyImage,
          showText: readyShowText,
          onStart: () => {
            overlay.remove();

            // Show follow-up overlay first, then start the game when user confirms
            const followUpOverlay = createFollowUpOverlay({
              forwardText: resolveOverlayText(followUpForward),
              onContinue: () => {
                try {
                  game.startPhase0();
                  if (firstPhase >= 2) {
                    game.matchDriverAndTargetSpeed();
                    if (typeof game.updateCoordinates === "function") {
                      game.updateCoordinates();
                    }
                  }
                } catch (e) {
                  console.warn("Failed to initialize board:", e);
                }
                jsPsych.finishTrial();
              },
            });
            document.body.appendChild(followUpOverlay);
          },
        });

        document.body.appendChild(overlay);
      },
      on_finish: function (data) {
        data.name = "readyOverlay";
      },
    };
    timeline.push(readyTrial);
  }

  // Build each requested phase trial, wiring it to the current runtime game instance
  for (const p of phases) {
    const builder = buildersMap[p];
    if (!builder) continue;

    if (p === 0) {
      const phase0Trial = {
        choices: "NO_KEYS",
        on_load: function () {
          window._gear_stopRemaining = false;
          const game = createFreshGame();
          prepareBoardPreview(game, 0);

          const startPhaseZero = () => {
            try {
              game.startPhase0();
            } catch (e) {
              console.error("Error starting phase0:", e);
            }
            const phaseTimeout = setTimeout(() => {
              jsPsych.finishTrial();
            }, game.targetLaunchDelay);

            window._gear_phase0_timeout = function () {
              if (phaseTimeout) clearTimeout(phaseTimeout);
            };
          };

          // If followUpOnly is set, show only the follow-up overlay without readyOverlay/video
          if (followUpOnly) {
            const followUpOverlay = createFollowUpOverlay({
              forwardText: resolveOverlayText(followUpForward),
              onContinue: startPhaseZero,
            });
            document.body.appendChild(followUpOverlay);
            return;
          }

          const overlay = createReadyOverlay({
            forwardText: resolveOverlayText(readyForward),
            backText: resolveOverlayText(readyBack),
            imageOrVideoUrl: readyImage,
            showText: readyShowText,
            onStart: () => {
              overlay.remove();
              const followUpOverlay = createFollowUpOverlay({
                forwardText: resolveOverlayText(followUpForward),
                onContinue: startPhaseZero,
              });
              document.body.appendChild(followUpOverlay);
            },
          });
          document.body.appendChild(overlay);
        },
        on_finish: function (data) {
          try {
            if (p === lastPhase) {
              const display = document.querySelector(".jspsych-display-element") || document.body;
              const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
              document.body.appendChild(preserved);
            }
          } catch (e) {
            console.warn("buildTutorialWithPhasesMissionFive: failed to preserve final frame (phase0):", e);
          }

          try {
            const clearFn = window._gear_phase0_timeout;
            if (typeof clearFn === "function") clearFn();
          } catch (e) {
            console.warn("buildTutorialWithPhasesMissionFive: error clearing phase0 timeout:", e);
          }

          try {
            const game = window._gear_currentGame;
            data.name = "initializeTarget";
            if (game) {
              data.driverSpeed = game.driverCarSpeed;
              data.distanceToTarget = game.driverDistanceToTarget;
              data.trialStart = {
                name: "trialStart",
                driverSpeed: game.driverCarSpeed,
                timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
              };
            }
          } catch (e) {
            console.warn("buildTutorialWithPhasesMissionFive: error assigning phase0 finish data:", e);
          }

          if (hooks.onPhaseFinish) hooks.onPhaseFinish(p, data, window._gear_currentGame);
        },
      };
      timeline.push(phase0Trial);
      continue;
    }

    // for other phases, construct trials that use `window._gear_currentGame`
    const phaseTrials = builder({}, jsPsych, score);
    for (const t of phaseTrials) {
      const cloned = Object.assign({}, t);

      if (p === 2) {
        cloned.trial_duration = function () {
          const base = window._gear_currentGame ? window._gear_currentGame.csi || 0 : 0;
          const crashed = window._gear_crashed;
          const shouldAddDelay = customOptions.addPhase2Delay && !crashed;
          return base + (shouldAddDelay ? 2000 : 0);
        };
      } else if (typeof t.trial_duration === "number") {
        cloned.trial_duration = t.trial_duration;
      }

      cloned.on_load = function () {
        try {
          // Each phase re-renders the stimulus, so re-apply dynamic key placeholders every load.
          applyTutorialHeaderPlaceholders(jsPsych);

          if (!window._gear_currentGame) createFreshGame();
          const game = window._gear_currentGame;

          if (p !== 6) {
            removeSurveyPanel();
          }

          switch (p) {
            case 1:
              game.startPhase1();
              break;
            case 2:
              game.startPhase2();
              break;
            case "0.1":
              game.startPhase0_1M5();
              break;
            case "0.2":
              game.startPhase0_2M5();
              break;
            case "0.3":
              game.startPhase0_3M5();
              break;
            case "0.4":
              game.startPhase0_4M5();
              break;
            case "0.4b": {
              const last = jsPsych.data.get().last(1).values()[0] || {};
              game._tutorialPartnerKeyCode = last.partnerKeyCode;
              game.startPhase0_4BM5(game._tutorialPartnerKeyCode);
              break;
            }
            case "0.5": {
              let laneCodes;
              if (game.cooperating === 1) {
                const lastTrialData = jsPsych.data.get().last(3).values();
                laneCodes = [lastTrialData[2]?.userKeyCode, lastTrialData[0]?.partnerKeyCode];
              } else {
                const lastTrialData = jsPsych.data.get().last(1).values();
                laneCodes = [lastTrialData[0]?.userKeyCode];
              }
              game._tutorialLaneCodes = laneCodes;
              game.startPhase0_5M5(laneCodes);
              break;
            }
            case "0.6": {
              let laneCodes;
              if (game.cooperating === 1) {
                const lastTrialData = jsPsych.data.get().last(4).values();
                laneCodes = [lastTrialData[2]?.userKeyCode, game.moveTruckLane];
              } else {
                const lastTrialData = jsPsych.data.get().last(2).values();
                laneCodes = [lastTrialData[0]?.userKeyCode];
              }
              game._tutorialPhase06LaneCodes = laneCodes;
              game.startPhase0_6M5(laneCodes);
              break;
            }
            case 6:
              game.startPhase(6);
              hideElement(game.targetCar);
              hideElement(game.agentImage);
              game.playEffect("message");
              injectSurveyPanel(jsPsych, {
                portrait: "hq-happy",
                promptText: "How are you?",
              });
              break;
            case 7:
              game.startPhase7();
              break;
            default:
              break;
          }

          // Capture snapshot after phase start so fallback includes the rendered phase state.
          try {
            applyTutorialHeaderPlaceholders(jsPsych);
            const display = document.querySelector(".jspsych-display-element") || document.body;
            const html = display.innerHTML;
            const len = html?.length || 0;
            if (len >= 200) {
              window._gear_preservedFrameHTML_phaseStart = html;
              window._gear_preservedFrameHTML_phaseStart_len = len;
            }
          } catch (e) {
            console.warn("buildTutorialWithPhasesMissionFive: failed to capture phase start snapshot", e);
          }

          if (hooks.onPhaseStart) hooks.onPhaseStart(p, game);
        } catch (e) {
          console.warn(`Error starting mission 5 tutorial phase ${p}:`, e);
        }
      };

      // on_finish use runtime game state and preserve frame
      cloned.on_finish = function (data) {
        try {
          if (p === lastPhase) {
            const display = document.querySelector(".jspsych-display-element") || document.body;
            const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
            const snapshotLength = preserved.innerHTML?.length || 0;
            const previousLength = window._gear_preservedFrameHTML
              ? window._gear_preservedFrameHTML.length
              : 0;

            if (snapshotLength < 200) {
              if (window._gear_preservedFrameHTML_phaseStart_len >= 200) {
                const fallbackPreserved = createPreservedFrame({
                  html: window._gear_preservedFrameHTML_phaseStart,
                  display,
                  score,
                });
                const existing = document.getElementById("gear-preserved-frame");
                if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
                document.body.appendChild(fallbackPreserved);
                window._gear_preservedFrameHTML = fallbackPreserved.innerHTML;
              }
            } else {
              const existing = document.getElementById("gear-preserved-frame");
              if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
              document.body.appendChild(preserved);
              if (snapshotLength >= previousLength) {
                window._gear_preservedFrameHTML = preserved.innerHTML;
              }
            }
          }

          if (p === 1 && data && data.crashed) {
            try {
              window._gear_crashed = true;
              const display = document.querySelector(".jspsych-display-element") || document.body;
              if (!document.getElementById("gear-preserved-frame")) {
                const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
                document.body.appendChild(preserved);
              }
            } catch (e) {
              console.warn("buildTutorialWithPhasesMissionFive: failed to preserve frame on crash:", e);
            }
          }

          // Phase-specific data assignments using the runtime game instance
          const game = window._gear_currentGame;
          if (game) {
            switch (p) {
              case 1:
                data.name = "engageTarget";
                data.cueCarOn = {
                  name: "cueCarOn",
                  driverSpeed: game.driverCarSpeed,
                  timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
                  distanceToTarget: game.driverDistanceToTarget,
                  csi: game.csi,
                };
                break;
              case 2:
                data.name = "licensePlateOn";
                data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
                data.driverSpeed = game.driverCarSpeed;
                data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
                break;
              case "0.1":
                data.name = "choiceCooperation";
                game.cooperating = (data.response_event_code ?? data.response) === "Slash" ? 1 : 0;
                data.cooperating = game.cooperating;
                if (
                  customOptions.requiredCooperationChoice === "cooperate"
                  && (data.response_event_code ?? data.response) !== "Slash"
                ) {
                  data.wrongLane = true;
                  window._gear_wrongLane = true;
                  window._gear_stopRemaining = true;
                  preserveFrameNow();
                }
                if (
                  customOptions.requiredCooperationChoice === "solo"
                  && (data.response_event_code ?? data.response) !== "KeyZ"
                ) {
                  data.wrongLane = true;
                  window._gear_wrongLane = true;
                  window._gear_stopRemaining = true;
                  preserveFrameNow();
                }
                break;
              case "0.2":
                data.name = "moveTruckCenter";
                break;
              case "0.3":
                data.name = "choiceLaneDriver";
                data.userKeyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null;
                break;
              case "0.4":
                data.name = "proposePartnerLane";
                data.partnerKeyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null;
                break;
              case "0.4b":
                data.name = "confirmPartnerLane";
                break;
              case "0.5":
                data.name = "movingIntoLanes";
                data.moveTruckLane = game.moveTruckLane;
                break;
              case "0.6":
                data.name = "cooperationOutcome";
                try {
                  const driverImage = document.getElementById("driverImage");
                  const truckImage = document.getElementById("truckImage");
                  const driverImageSrc = driverImage?.getAttribute("src") || "";
                  const truckImageSrc = truckImage?.getAttribute("src") || "";
                  if (
                    driverImageSrc.includes("collisionDriver") ||
                    truckImageSrc.includes("crashTruck")
                  ) {
                    data.crashed = true;
                  }
                } catch (e) {
                  console.warn("buildTutorialWithPhasesMissionFive: failed to read 0.6 crash state", e);
                }
                // Fail after phase 0.6 if driver and partner selected the same lane.
                if (customOptions.requiredCooperationChoice === "cooperate" && game.cooperating === 1) {
                  const tutorialLaneCodes = game._tutorialLaneCodes || [];
                  const driverLane = tutorialLaneCodes[0] ?? null;
                  const partnerLane = tutorialLaneCodes[1] ?? null;
                  if (driverLane !== null && partnerLane !== null && driverLane === partnerLane) {
                    data.wrongLane = true;
                    window._gear_wrongLane = true;
                    window._gear_stopRemaining = true;
                    preserveFrameNow();
                  }
                }
                break;
              case 6:
                removeSurveyPanel();
                data.name = "messageFromHQ";
                break;
              case 7:
                data.name = "driverAtCenter";
                data.driverToCenter = {
                  timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
                  driverSpeed: game.driverCarSpeed,
                };
                data.driverSpeed = game.driverCarSpeed;
                break;
              default:
                break;
            }

            if (data.crashed) {
              window._gear_crashed = true;
              window._gear_stopRemaining = true;
              preserveFrameNow();
            }
          }
        } catch (e) {
          console.warn(`buildTutorialWithPhasesMissionFive: error in phase ${p} on_finish:`, e);
        }

        if (hooks.onPhaseFinish) hooks.onPhaseFinish(p, data, window._gear_currentGame);
      };

      timeline.push({
        timeline: [cloned],
        conditional_function: function () {
          return !window._gear_stopRemaining;
        },
      });
    }
  }

  // create a small congrats trial that displays an overlay
  const congratsTrial = {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus: "",
    choices: "NO_KEYS",
    on_load: () => {
      let pres = document.getElementById("gear-preserved-frame");
      if (!pres) {
        try {
          const display = document.querySelector(".jspsych-display-element") || document.body;
          const html =
            window._gear_preservedFrameHTML ||
            window._gear_preservedFrameHTML_phaseStart ||
            display.innerHTML;
          const preserved = createPreservedFrame({ html, display, score });
          document.body.appendChild(preserved);
          pres = preserved;
        } catch (e) {
          console.warn("buildTutorialWithPhasesMissionFive: failed to preserve frame on success path", e);
          return;
        }
      }

      const crashed = !!window._gear_crashed;
      const wrongLane = !!window._gear_wrongLane;
      const tryAgain = crashed || wrongLane;

      // Check fails limit before showing overlay
      if (hooks.onBeforeCongratsOverlay) {
        try {
          const shouldInterrupt = hooks.onBeforeCongratsOverlay({ tryAgain, crashed, wrongLane });
          if (shouldInterrupt === true) {
            return;
          }
        } catch (e) {
          console.warn("buildTutorialWithPhasesMissionFive: onBeforeCongratsOverlay failed:", e);
        }
      }

      const overlay = createCongratsOverlay({
        forwardText: resolveOverlayText(tryAgain ? congratsForwardCrash : congratsForward),
        backText: resolveOverlayText(tryAgain ? congratsBackCrash : congratsBack),
        imageOrVideoUrl: tryAgain ? congratsImageCrash : congratsImage,
        playCelebrate: !tryAgain,
        showText: congratsShowText,
        onContinue: () => {
          const preserved = document.getElementById("gear-preserved-frame");
          if (preserved) preserved.parentNode.removeChild(preserved);

          if (typeof window._gear_phase0_timeout === "function") window._gear_phase0_timeout();
          jsPsych.finishTrial();
        },
      });

      document.body.appendChild(overlay);
    },
    on_finish: (data) => {
      if (hooks.onPhaseFinish) hooks.onPhaseFinish("congrats", data, window._gear_currentGame);
    },
  };

  timeline.push(congratsTrial);

  // Wrap the phases timeline in a looping node which will repeat the entire
  // set of phases when a crash was detected
  // the loop_function receives the data from the
  // last iteration to decide if needs to run the timeline again
  return {
    timeline: [
      {
        type: CustomHtmlKeyboardResponsePlugin,
        stimulus,
        data: { trialConfig, tutorial: true },
        timeline,
      },
    ],
    loop_function: function (data) {
      // if any trial in the previous iteration flagged `crashed` or `wrongLane`, repeat.
      try {
        const values = data.values();
        const crashed = values.some((v) => !!v.crashed);
        const wrongLaneFromData = values.some((v) => !!v.wrongLane);
        const wrongLaneGlobal = !!window._gear_wrongLane;
        let shouldRepeat = crashed || wrongLaneFromData || wrongLaneGlobal;

        if (hooks.onLoopDecide) {
          try {
            const maybe = hooks.onLoopDecide({
              data,
              crashed,
              wrongLaneFromData,
              wrongLaneGlobal,
              defaultShouldRepeat: shouldRepeat,
            });
            if (typeof maybe === "boolean") shouldRepeat = maybe;
          } catch (e) {
            console.warn("buildTutorialWithPhasesMissionFive: onLoopDecide failed:", e);
          }
        }

        window._gear_crashed = false;
        window._gear_wrongLane = false;
        window._gear_stopRemaining = false;

        return shouldRepeat;
      } catch (e) {
        console.warn("buildTutorialWithPhasesMissionFive: loop_function error:", e);
        return false;
      }
    },
  };
}
